import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/tour.css';
import { useAuth } from './AuthContext';
import { useCompleteTour } from '../api/hooks/useUser';

interface TourContextType {
  startBuilderTour: () => void;
  hasCompletedBuilderTour: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const completeTourMutation = useCompleteTour();

  const [hasCompletedBuilderTour, setHasCompletedBuilderTour] = useState(() => {
    return user?.builder_tour_completed ?? false;
  });

  // Sync state with user data when it changes
  useEffect(() => {
    setHasCompletedBuilderTour(user?.builder_tour_completed ?? false);
  }, [user?.builder_tour_completed]);

  const startBuilderTour = useCallback(() => {
    // Check for force tour URL parameter (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const forceTour = urlParams.get('tour') === '1';

    console.log('[Tour] startBuilderTour called', { forceTour, hasCompletedBuilderTour });

    // Skip tour if already completed (unless forced)
    if (!forceTour && hasCompletedBuilderTour) {
      console.log('[Tour] Skipping - already completed');
      return;
    }

    console.log('[Tour] Starting tour...');

    // Check if we're on /create (no guests button available)
    const isCreateMode = window.location.pathname === '/create';
    console.log('[Tour] isCreateMode:', isCreateMode, 'pathname:', window.location.pathname);

    const allSteps: DriveStep[] = [
      {
        element: '[data-tour="theme-selector"]',
        popover: {
          title: 'Choose Your Theme',
          description: 'Start by selecting a beautiful theme that matches your event style. Each theme includes coordinated colors, fonts, and patterns.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="theme-preview"]',
        popover: {
          title: 'Live Preview',
          description: 'See your invite update in real-time as you make changes. What you see here is exactly what your guests will see.',
          side: 'left',
          align: 'start',
          onNextClick: () => {
            // Open Fonts & Colors accordion BEFORE moving to palette step
            const accordion = document.querySelector('[data-tour="color-customization"]') as HTMLElement;
            const isOpen = document.querySelector('[data-tour="palette-customization"]');
            if (accordion && !isOpen) {
              accordion.click();
              // Wait for animation, then move to next step
              setTimeout(() => driverObj.moveNext(), 350);
            } else {
              driverObj.moveNext();
            }
          },
        },
      },
      {
        element: '[data-tour="palette-customization"]',
        popover: {
          title: 'Choose Color Palette',
          description: 'Choose from curated color palettes or create your own custom palette to match your event perfectly.',
          side: 'right',
          align: 'start',
          onNextClick: () => {
            // Open Fonts & Colours accordion before moving to font step
            const fontEl = document.querySelector('[data-tour="font-customization"]');
            if (!fontEl) {
              const accordion = document.querySelector('[data-tour="color-customization"]') as HTMLElement;
              accordion?.click();
              setTimeout(() => driverObj.moveNext(), 350);
            } else {
              driverObj.moveNext();
            }
          },
        },
        onHighlightStarted: () => {
          // Open Palettes accordion if needed
          const isOpen = document.querySelector('[data-tour="palette-customization"]');
          if (!isOpen) {
            const accordion = document.querySelector('[data-tour="color-customization"]') as HTMLElement;
            accordion?.click();
          }
        },
      },
      {
        element: '[data-tour="font-customization"]',
        popover: {
          title: 'Select Fonts',
          description: 'Pick font pairings that complement your theme. Each pairing includes a heading and body font designed to work together.',
          side: 'right',
          align: 'start',
          onNextClick: () => {
            // Open Hero accordion BEFORE moving to content editor step
            const heroAccordion = document.querySelector('[data-tour="hero-accordion"]') as HTMLElement;
            const isOpen = document.querySelector('[data-tour="content-editor"]');
            if (heroAccordion && !isOpen) {
              heroAccordion.click();
              // Wait for animation, then move to next step
              setTimeout(() => driverObj.moveNext(), 350);
            } else {
              driverObj.moveNext();
            }
          },
          onPrevClick: () => {
            // Going back to palette - accordion should stay open
            driverObj.movePrevious();
          },
        },
        onHighlightStarted: () => {
          // Ensure Fonts & Colors accordion is open
          const isOpen = document.querySelector('[data-tour="font-customization"]');
          if (!isOpen) {
            const accordion = document.querySelector('[data-tour="color-customization"]') as HTMLElement;
            accordion?.click();
          }
        },
      },
      {
        element: '[data-tour="content-editor"]',
        popover: {
          title: 'Edit Your Content',
          description: 'Fill in your event details, add photos, and customize the content for each section of your invite.',
          side: 'left',
          align: 'start',
          onNextClick: () => {
            driverObj.moveNext();
          },
          onPrevClick: () => {
            // Going back to fonts - ensure Fonts & Colors accordion is open
            const accordion = document.querySelector('[data-tour="color-customization"]') as HTMLElement;
            const isOpen = document.querySelector('[data-tour="font-customization"]');
            if (accordion && !isOpen) {
              accordion.click();
              setTimeout(() => driverObj.movePrevious(), 350);
            } else {
              driverObj.movePrevious();
            }
          },
        },
        onHighlightStarted: () => {
          // Open Hero accordion if needed
          const isOpen = document.querySelector('[data-tour="content-editor"]');
          if (!isOpen) {
            const heroAccordion = document.querySelector('[data-tour="hero-accordion"]') as HTMLElement;
            heroAccordion?.click();
          }
        },
      },
      {
        element: '[data-tour="sections-toggle"]',
        popover: {
          title: 'Manage Sections',
          description: 'Enable or disable sections like Schedule, RSVP, FAQs, and Gift Registry based on what you need for your event.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="guests-button"]',
        popover: {
          title: 'Manage Guests',
          description: 'Add guests, track RSVPs, and manage your guest list. Send personalized invitations to each guest.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="preview-button"]',
        popover: {
          title: 'Preview Your Invite',
          description: 'See how your invite will look to guests. Preview it in a new tab before publishing.',
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '[data-tour="device-picker"]',
        popover: {
          title: 'View on Different Devices',
          description: 'Switch between mobile, tablet, and desktop views to see how your invite looks on different screen sizes.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="save-publish"]',
        popover: {
          title: 'Publish Your Invite',
          description: 'Click this to publish your invite. Once published, you can manage visibility and sharing options.',
          side: 'bottom',
          align: 'end',
        },
        onHighlightStarted: () => {
          document.body.setAttribute('data-tour-publish-active', 'true');
        },
        onNextClick: () => {
          document.body.setAttribute('data-tour-publish-active', 'true');
          // Wait for dropdown to render before advancing
          const poll = (remaining: number) => {
            if (document.querySelector('[data-tour="publish-visibility"]')) {
              driverObj.moveNext();
            } else if (remaining > 0) {
              setTimeout(() => poll(remaining - 50), 50);
            } else {
              driverObj.moveNext(); // give up and advance anyway
            }
          };
          poll(500);
        },
      },
      {
        element: '[data-tour="publish-visibility"]',
        popover: {
          title: 'Public or Private',
          description: 'Choose whether your invite is public (anyone with the link) or private (only invited guests can view).',
          side: 'left',
          align: 'start',
        },
        onHighlightStarted: () => {
          // Ensure flag is still set
          document.body.setAttribute('data-tour-publish-active', 'true');
        },
      },
      {
        element: '[data-tour="publish-url"]',
        popover: {
          title: 'Share Public Invites',
          description: 'Customize your invite URL and easily share it via the copy button, email, or WhatsApp. Make the slug memorable for your guests.',
          side: 'left',
          align: 'start',
        },
        onHighlightStarted: () => {
          // Ensure flag is still set
          document.body.setAttribute('data-tour-publish-active', 'true');
          // Switch to public mode if needed (URL section only shows in public mode)
          const urlElement = document.querySelector('[data-tour="publish-url"]');
          if (!urlElement) {
            // Not visible, need to click public button
            const visibilitySection = document.querySelector('[data-tour="publish-visibility"]');
            const publicButton = visibilitySection?.querySelector('button:first-child') as HTMLElement;
            if (publicButton) {
              publicButton.click();
            }
          }
        },
      },
      {
        popover: {
          title: 'You\'re All Set! 🎉',
          description: 'You now know the basics of creating beautiful invites. Start building your invite and explore all the customization options!',
        },
      },
    ];

    // Filter out specific steps that don't exist on /create
    const steps = allSteps.filter(step => {
      if (!step.element) return true; // Keep popover-only steps

      // On /create, skip these elements (don't exist or require auth)
      if (isCreateMode) {
        const skipElements = [
          '[data-tour="guests-button"]',
          '[data-tour="publish-visibility"]',
          '[data-tour="publish-url"]'
        ];
        if (typeof step.element === 'string' && skipElements.includes(step.element)) {
          console.log('[Tour] Filtering out', step.element, 'on /create');
          return false;
        }
      }

      return true; // Keep all other steps
    });

    console.log('[Tour] Total steps:', allSteps.length, '→ Filtered steps:', steps.length);

    const driverObj = driver({
      showProgress: true,
      stagePadding: 10,
      stageRadius: 8,
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      smoothScroll: true,
      steps,
      onDestroyStarted: () => {
        // Clean up tour flags
        document.body.removeAttribute('data-tour-publish-active');
        // Track completion in backend (only if authenticated)
        if (user) {
          completeTourMutation.mutate();
        }
        setHasCompletedBuilderTour(true);
        driverObj.destroy();
      },
    });

    driverObj.drive();
  }, [completeTourMutation, hasCompletedBuilderTour]);

  return (
    <TourContext.Provider value={{ startBuilderTour, hasCompletedBuilderTour }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}
